import { createDpopLoginKeyPair } from '../dpop/loginBinding';
import { clearDpopKeyPair, isDpopSupported, readDpopKeyPair, saveDpopKeyPair } from '../dpop/store';
import { executeFetch } from '../utils/fetch/fetch';
import type { QueryMeta } from '../utils/fetch/types';
import getDomain from '../utils/url/getDomain';

interface BindDpopKeyResponse {
  // the session kept a key of its own, so the one we offered was not bound
  alreadyBound?: boolean;
}

interface BindDpopKeyOptions {
  // external sessions bind via the external prefix and re-offer a held key (see README)
  isExternalSession?: boolean;
}

// binds a key to a session whose login had no body to carry one. Never throws.
export async function bindDpopKey(meta?: QueryMeta, options?: BindDpopKeyOptions): Promise<void> {
  // the same origin getBaseURL will send the bind to: file it anywhere else and the session signs
  // with a key the request hook never looks up, unsigned and with no error to notice
  const keyOrigin = getDomain(meta);
  const storedKeyPair = await readDpopKeyPair(keyOrigin);

  // for platform sessions a stored key was already bound by a login
  if (storedKeyPair && !options?.isExternalSession) return;

  // re-offered keys still respect the opt-in flag
  if (storedKeyPair && !(await isDpopSupported())) return;

  const keyPair = storedKeyPair ?? (await createDpopLoginKeyPair());
  if (!keyPair) return;

  const isNewKeyPair = !storedKeyPair;
  // saved before the request, not after: a bind that works but loses its reply is unfixable
  if (isNewKeyPair) await saveDpopKeyPair(keyPair, keyOrigin);

  try {
    // pins the url rewrites: /api for platform sessions, external for `_aut` ones
    const bindMeta = options?.isExternalSession
      ? { ...meta, isPublicInterface: true }
      : { ...meta, isPublicInterface: false, isUserLoggedIn: true };
    const response = await executeFetch<BindDpopKeyResponse>(
      { url: '/api/session/bind-dpop-key', method: 'POST' },
      { meta: bindMeta, dpopKeyPair: keyPair },
    );

    // only a key created here is dropped — a re-offered one is already the bound key
    if (response.alreadyBound && isNewKeyPair) {
      const stored = await readDpopKeyPair(keyOrigin);
      if (stored?.publicKey.x === keyPair.publicKey.x) {
        await clearDpopKeyPair(keyOrigin);
      }
    }
  } catch (error) {
    // the saved key stays — this request may have bound it and lost the reply
    console.warn('Failed to link the auth key to this session:', error);
  }
}
